import { alpha } from '@mui/material/styles';

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
    main: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
    main: string;
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }
  interface Palette {
    sky: PaletteColor;
    melon: PaletteColor;
    mora: PaletteColor;
    sandia: PaletteColor;
    limon: PaletteColor;
    menta: PaletteColor;
    dalia: PaletteColor;
  }
  interface PaletteOptions {
    sky?: Partial<PaletteColor>;
    melon?: Partial<PaletteColor>;
    mora?: Partial<PaletteColor>;
    sandia?: Partial<PaletteColor>;
    limon?: Partial<PaletteColor>;
    menta?: Partial<PaletteColor>;
    dalia?: Partial<PaletteColor>;
  }
}

declare module '@mui/material' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
    main: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
    main: string;
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }
  interface Palette {
    sky: PaletteColor;
    melon: PaletteColor;
    mora: PaletteColor;
    sandia: PaletteColor;
    limon: PaletteColor;
    menta: PaletteColor;
    dalia: PaletteColor;
  }
  interface PaletteOptions {
    sky?: Partial<PaletteColor>;
    melon?: Partial<PaletteColor>;
    mora?: Partial<PaletteColor>;
    sandia?: Partial<PaletteColor>;
    limon?: Partial<PaletteColor>;
    menta?: Partial<PaletteColor>;
    dalia?: Partial<PaletteColor>;
  }
}

// SETUP COLORS

const GREY = {
  main: '#9697AF',
  50: '#F2F3F5',
  100: '#E0E0E7',
  200: '#CBCBD7',
  300: '#B6B6C7',
  400: '#A6A7BB',
  500: '#9697AF',
  600: '#8E8FA8',
  700: '#83849F',
  800: '#797A96',
  900: '#686986',
};

const PRIMARY = {
  main: '#85C440',
  50: '#F0F8E8',
  100: '#DAEDC6',
  200: '#C2E2A0',
  300: '#AAD679',
  400: '#97CD5D',
  500: '#85C440',
  600: '#7DBE3A',
  700: '#72B632',
  800: '#68AF2A',
  900: '#55A21C',
  darker: '#55A21C',
  dark: '#7DBE3A',
  light: '#DAEDC6',
  lighter: '#F0F8E8',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  light: '#F5F5F5',
  main: '#12344A',
  50: '#e3e7e9',
  100: '#b8c2c9',
  200: '#899aa5',
  300: '#597180',
  400: '365265',
  500: '#12344A',
  600: '#102f43',
  700: '#0d273a',
  800: '#0a2132',
  900: '#051522',
  dark: '#262927',
  darker: '#051522',
  lighter: '#e3e7e9',
  contrastText: '#FFFFFF',
};

const SKY = {
  main: '#ADD8E5',
};

const MORA = {
  main: '#12344A',
};

const MELON = {
  main: '#FCB970',
};

const SANDIA = {
  main: '#FB728C',
};

const DALIA = {
  main: '#EC645E',
  light: 'rgb(236, 100, 94, 0.3)',
};

const LIMON = {
  lighter: '#6bcf3c',
  main: '#3FC468',
  light: '#aadb40',
};

const MENTA = {
  main: '#20C9A7',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#FFFFFF',
  background: '#d9eddf',
};

const WARNING = {
  lighter: '#FFF9CC',
  light: '#FFEB66',
  main: '#FFE41A',
  dark: '#B79E00',
  darker: '#7A6600',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FAD1D8',
  light: '#F2768A',
  main: '#E83154',
  dark: '#A3203A',
  darker: '#5A1120',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  sky: SKY,
  melon: MELON,
  mora: MORA,
  sandia: SANDIA,
  dalia: DALIA,
  limon: LIMON,
  menta: MENTA,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette() {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: '#12344A',
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: '#FFFFFF', default: '#FFFFFF', neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  //   const dark: PaletteOptions = {
  //     ...COMMON,
  //     mode: 'dark',
  //     text: {
  //       primary: '#FFFFFF',
  //       secondary: GREY[500],
  //       disabled: GREY[600],
  //     },
  //     background: {
  //       paper: GREY[800],
  //       default: GREY[900],
  //       neutral: alpha(GREY[500], 0.16),
  //     },
  //     action: {
  //       ...COMMON.action,
  //       active: GREY[500],
  //     },
  //   };

  return light;
}
